import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { gbp, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrustBadge } from "@/components/TrustBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Check, Pause, Trash2, Snowflake, UserX, FileText, ShieldAlert } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [v5c, setV5c] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, q, r, u] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/review-queue"), api.get("/admin/reports"), api.get("/admin/users"),
      ]);
      setStats(s.data); setQueue(q.data); setReports(r.data); setUsers(u.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const listingAct = async (id, action, params) => {
    try { await api.post(`/admin/listings/${id}/${action}`, null, { params }); toast.success("Done"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const userAct = async (id, action, params) => {
    try { await api.post(`/admin/users/${id}/${action}`, null, { params }); toast.success("Done"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const resolveReport = async (id) => { try { await api.post(`/reports/${id}/resolve`); toast.success("Resolved"); load(); } catch (e) {} };

  const viewV5c = async (id) => {
    try {
      const token = localStorage.getItem("vorra_token");
      const res = await fetch(`${API}/admin/v5c/${id}`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      setV5c(URL.createObjectURL(blob));
    } catch (e) { toast.error("Could not load V5C"); }
  };

  const Stat = ({ label, value }) => (
    <div className="bg-card border border-border rounded-xl p-4"><div className="text-xs text-muted-foreground">{label}</div><div className="font-heading text-2xl font-bold mt-1">{value}</div></div>
  );

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-heading text-3xl font-bold flex items-center gap-2"><ShieldAlert className="w-7 h-7 text-accent" />Admin console</h1>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mt-6">
        <Stat label="Users" value={stats?.total_users} />
        <Stat label="Listings" value={stats?.total_listings} />
        <Stat label="Live" value={stats?.live_listings} />
        <Stat label="In review" value={stats?.pending_review} />
        <Stat label="Subs" value={stats?.active_subscriptions} />
        <Stat label="Reports" value={stats?.open_reports} />
        <Stat label="Frozen" value={stats?.frozen_accounts} />
      </div>

      <Tabs defaultValue="queue" className="mt-8">
        <TabsList><TabsTrigger value="queue" data-testid="tab-queue">Review queue ({queue.length})</TabsTrigger><TabsTrigger value="reports" data-testid="tab-reports">Reports ({reports.filter(r => r.status === "open").length})</TabsTrigger><TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger></TabsList>

        <TabsContent value="queue" className="mt-4 space-y-3">
          {queue.length === 0 ? <p className="text-muted-foreground py-8 text-center">Nothing to review 🎉</p> : queue.map((l) => (
            <div key={l.id} className="bg-card border border-border rounded-xl p-4 flex items-start gap-4" data-testid={`queue-item-${l.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap"><span className="font-heading font-semibold">{l.make} {l.model} {l.year}</span><TrustBadge type={l.status} />{l.reg && <span className="uk-plate px-2 py-0.5 text-sm">{l.reg}</span>}</div>
                <p className="text-sm text-muted-foreground mt-1">{gbp(l.price)} · {Number(l.mileage).toLocaleString()} mi · {l.seller_type}</p>
                {l.hold_reason && <p className="text-sm text-amber-700 mt-1">⚠ {l.hold_reason}</p>}
                {l.lookup_conflict && <p className="text-xs text-red-600 mt-1">Data conflicts with plate lookup.</p>}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button size="sm" onClick={() => listingAct(l.id, "approve")} className="bg-accent hover:bg-emerald-600 text-white" data-testid={`approve-${l.id}`}><Check className="w-4 h-4 mr-1" />Approve</Button>
                {l.v5c_status === "pending" && <Button size="sm" variant="outline" onClick={() => viewV5c(l.id)} data-testid={`view-v5c-${l.id}`}><FileText className="w-4 h-4 mr-1" />V5C</Button>}
                <Button size="sm" variant="outline" onClick={() => listingAct(l.id, "hold", { reason: "Held for document review" })} data-testid={`hold-${l.id}`}><Pause className="w-4 h-4 mr-1" />Hold</Button>
                <Button size="sm" variant="ghost" onClick={() => listingAct(l.id, "remove")} data-testid={`admin-remove-${l.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="reports" className="mt-4 space-y-3">
          {reports.length === 0 ? <p className="text-muted-foreground py-8 text-center">No reports.</p> : reports.map((r) => (
            <div key={r.id} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between" data-testid={`report-${r.id}`}>
              <div><p className="font-medium capitalize">{r.target_type} report</p><p className="text-sm text-muted-foreground">{r.reason} · by {r.reporter_name}</p></div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded ${r.status === "open" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{r.status}</span>
                {r.status === "open" && <Button size="sm" variant="outline" onClick={() => resolveReport(r.id)} data-testid={`resolve-${r.id}`}>Resolve</Button>}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="users" className="mt-4 space-y-2">
          {users.map((u) => (
            <div key={u.user_id} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`user-${u.user_id}`}>
              <div>
                <p className="font-medium">{u.name} <span className="text-xs text-muted-foreground">· {u.email}</span></p>
                <div className="flex gap-2 mt-1 text-xs">
                  <span className="bg-slate-100 px-2 py-0.5 rounded">{u.role}</span>
                  {u.seller_type && <span className="bg-slate-100 px-2 py-0.5 rounded">{u.seller_type}</span>}
                  {u.account_frozen && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">Frozen</span>}
                  {u.forced_dealer_review && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Trader review</span>}
                  {u.chargeback_flag && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">Chargeback</span>}
                </div>
              </div>
              {u.role !== "admin" && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => userAct(u.user_id, "freeze", { freeze: !u.account_frozen })} data-testid={`freeze-${u.user_id}`}><Snowflake className="w-4 h-4 mr-1" />{u.account_frozen ? "Unfreeze" : "Freeze"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => userAct(u.user_id, "flag-trader")} data-testid={`flag-${u.user_id}`}><UserX className="w-4 h-4 mr-1" />Flag trader</Button>
                </div>
              )}
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!v5c} onOpenChange={(o) => !o && setV5c(null)}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle>V5C document</DialogTitle></DialogHeader>
          {v5c && <iframe src={v5c} title="V5C" className="w-full h-[70vh] rounded-lg border border-border" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
